/**
 * Customer-facing label translations for device recognition results.
 *
 * The backend uses internal keys (snake_case, technical taxonomy) so it can
 * remain stable across vendors. These are never shown to customers directly.
 * Every customer-facing screen should run raw values through these helpers.
 *
 * No new data — purely a rendering mapping.
 */

const CAPABILITY_LABELS = {
  onoff: 'Aan/uit',
  target_temperature: 'Temperatuur instellen',
  measure_temperature: 'Temperatuur meten',
  measure_power: 'Stroomverbruik meten',
  meter_power: 'Totaalverbruik meten',
  measure_voltage: 'Spanning meten',
  measure_current: 'Stroomsterkte meten',
  measure_humidity: 'Luchtvochtigheid meten',
  measure_co2: 'CO₂ meten',
  measure_luminance: 'Lichtsterkte meten',
  dim: 'Dimmen',
  light_hue: 'Kleur instellen',
  light_saturation: 'Verzadiging instellen',
  light_temperature: 'Lichtwarmte instellen',
  windowcoverings_state: 'Gordijnen / zonwering',
  windowcoverings_set: 'Gordijnen / zonwering',
  vacuumcleaner_state: 'Stofzuiger sturen',
  alarm_motion: 'Bewegingsmelder',
  alarm_contact: 'Open/dicht melder',
  alarm_smoke: 'Rookmelder',
  thermostat_mode: 'Thermostaat stand',
  fan_speed: 'Ventilatorsnelheid',
  fan_mode: 'Ventilatie stand',
  ev_charging_state: 'Laadstatus',
  ev_charging_current: 'Laadstroom instellen',
  battery_state: 'Accu status',
  battery_charge_target: 'Accu laaddoel',
};

const CATEGORY_LABELS = {
  A_p1_meter: 'Slimme meter (P1)',
  B_smart_plugs: 'Slimme stekker',
  C_lighting: 'Verlichting',
  D_inverters: 'Omvormer',
  E_ev_chargers: 'Laadpaal',
  F_batteries: 'Thuisaccu',
  G_climate: 'Klimaat',
  H_boiler: 'Boiler',
  I_white_goods: 'Witgoed',
  Z_hub: 'Slimme hub',
  _unknown: 'Onbekend',
};

// device_key (snake_case rooms or device kinds) → klanttaal
const DEVICE_KEY_LABELS = {
  airco_living: 'Airco',
  airco_bedroom: 'Airco',
  airco_office: 'Airco',
  airco_indoor: 'Airco binnenunit',
  airco_outdoor: 'Airco buitenunit',
  heat_pump: 'Warmtepomp',
  ev_charger: 'Laadpaal',
  solar_inverter: 'Omvormer',
  battery_storage: 'Thuisaccu',
  smart_plug: 'Slimme stekker',
  p1_meter: 'Slimme meter (P1)',
  unknown_device: 'Apparaat',
};

// Free-text Gemini Vision device_type strings → klanttaal
// Keys are LOWERCASED; matched substring-style for resilience.
const DEVICE_TYPE_PATTERNS = [
  { test: /split.*air.*condition.*indoor/, label: 'Airco binnenunit' },
  { test: /split.*air.*condition.*outdoor/, label: 'Airco buitenunit' },
  { test: /air\s*condition/, label: 'Airco' },
  { test: /heat\s*pump/, label: 'Warmtepomp' },
  { test: /ev\s*charger|wallbox|charging\s*station/, label: 'Laadpaal' },
  { test: /solar\s*inverter|pv\s*inverter/, label: 'Omvormer' },
  { test: /home\s*battery|battery\s*storage/, label: 'Thuisaccu' },
  { test: /smart\s*plug|smart\s*socket/, label: 'Slimme stekker' },
  { test: /smart\s*meter|p1\s*meter/, label: 'Slimme meter (P1)' },
  { test: /boiler|water\s*heater/, label: 'Boiler' },
  { test: /washer|washing\s*machine/, label: 'Wasmachine' },
  { test: /dryer|tumble\s*dryer/, label: 'Wasdroger' },
  { test: /dishwasher/, label: 'Vaatwasser' },
  { test: /oven/, label: 'Oven' },
  { test: /refriger|fridge/, label: 'Koelkast' },
  { test: /hub|gateway|bridge/, label: 'Slimme hub' },
  { test: /lamp|light\s*bulb|led/, label: 'Verlichting' },
];

const CONTROL_STATUS_LABELS = {
  control_not_supported: 'Alleen meten',
  control_possible_later_requires_go: 'Sturing later mogelijk',
  control_blocked: 'Sturing geblokkeerd',
  control_allowed_with_consent: 'Sturing met toestemming',
};

const RECOGNITION_STATUS_LABELS = {
  recognized: 'Herkend',
  pending_review: 'Controle nodig',
  unknown_device: 'Niet herkend',
};

// Room labels — light-touch, only when we are confident
const ROOM_LABELS = {
  living: 'Woonkamer',
  bedroom: 'Slaapkamer',
  office: 'Kantoor',
  kitchen: 'Keuken',
  bathroom: 'Badkamer',
  garage: 'Garage',
};

function titleCase(s) {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function capabilityLabel(cap) {
  if (!cap) return '';
  return CAPABILITY_LABELS[cap] || titleCase(String(cap).replace(/_/g, ' '));
}

export function capabilitiesLabels(caps) {
  if (!Array.isArray(caps)) return [];
  return caps.map(capabilityLabel).filter(Boolean);
}

export function categoryLabel(cat) {
  if (!cat) return '';
  return CATEGORY_LABELS[cat] || titleCase(String(cat).replace(/_/g, ' '));
}

export function deviceKeyLabel(key) {
  if (!key) return '';
  if (DEVICE_KEY_LABELS[key]) return DEVICE_KEY_LABELS[key];
  // Strip room suffix and try again (e.g. airco_kitchen → Airco)
  const base = key.split('_').slice(0, -1).join('_');
  if (DEVICE_KEY_LABELS[base]) return DEVICE_KEY_LABELS[base];
  return titleCase(key.replace(/_/g, ' '));
}

export function deviceTypeLabel(raw) {
  if (!raw) return '';
  const lower = String(raw).toLowerCase();
  for (const p of DEVICE_TYPE_PATTERNS) {
    if (p.test.test(lower)) return p.label;
  }
  // No technical fallback — return empty so UI hides the field cleanly.
  return '';
}

export function controlStatusLabel(s) {
  return CONTROL_STATUS_LABELS[s] || '';
}

export function recognitionStatusLabel(s) {
  return RECOGNITION_STATUS_LABELS[s] || '';
}

export function roomLabel(key) {
  if (!key) return '';
  return ROOM_LABELS[String(key).toLowerCase()] || '';
}

// Tries to infer a room from the device_key (e.g. airco_living → Woonkamer).
export function roomFromDeviceKey(key) {
  if (!key) return '';
  const parts = String(key).toLowerCase().split('_');
  for (const part of parts) {
    const lbl = ROOM_LABELS[part];
    if (lbl) return lbl;
  }
  return '';
}

// Build a short, customer-facing device title.
// Example: brand=LG, deviceType="split room air conditioner indoor unit"
//          → "LG airco" (brand kept lowercase if model present).
export function customerDeviceTitle({ brand, deviceType, deviceKey } = {}) {
  const friendly = deviceTypeLabel(deviceType) || deviceKeyLabel(deviceKey);
  if (brand && friendly) return `${brand} ${friendly.toLowerCase()}`;
  return brand || friendly || 'Apparaat';
}

// Confidence → short word + tone (no number to avoid technical feel)
export function confidenceLabel(conf) {
  const c = Number(conf || 0);
  if (c >= 0.85) return { label: 'Herkend', tone: 'emerald' };
  if (c >= 0.55) return { label: 'Waarschijnlijk', tone: 'emerald' };
  if (c >= 0.30) return { label: 'Controle nodig', tone: 'amber' };
  return { label: 'Niet zeker', tone: 'amber' };
}
